import {
  WEBAPP_APP_NAME_MAX_CHARS,
  WEBAPP_ASSISTANT_ICON_AVATAR,
  WEBAPP_CARD_ICON_AUTO,
  WEBAPP_CARD_ICON_NONE,
  WEBAPP_ICON_VALUES,
  WEBAPP_LINE_MAX_CHARS,
  WEBAPP_MAX_STARTERS,
  WEBAPP_NOTE_MAX_CHARS,
  WEBAPP_PLACEHOLDER_MAX_CHARS,
  WEBAPP_STARTER_MAX_CHARS,
  WEBAPP_TITLE_MAX_CHARS,
} from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Segmented, cn, toast, type IconName } from "@sagot/ui";
import { useRef } from "react";
import { useUploadErrorMessage } from "../../../lib/upload-errors";
import { imageProblem } from "../widget-studio/brand-image";
import { BrandColorPicker } from "../widget-studio/brand-color-picker";
import { SettingSwitch } from "../widget-studio/setting-row";
import { useBrandImageUpload } from "../widget-studio/use-widget-studio";
import { AVATAR_MIME_TYPES } from "../widget-studio/widget-model";
import { moveItem, type WebappSettings, type WebappStudioOut } from "./webapp-model";

const K = "webappStudio.console";

export interface PanelProps {
  settings: WebappSettings;
  onChange: (next: WebappSettings) => void;
  design: WebappStudioOut;
  disabled: boolean;
  /** Editing the workspace default: pictures belong to one assistant. */
  isDefault: boolean;
  agentId: string;
}

type LocalKey = "title" | "subtitle" | "placeholder" | "disclaimer";

function Row({ label, hint, children, htmlFor }: { label: string; hint?: string; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div>
      <label className="label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

/** One text in English and Filipino. */
function LocalFields({ label, hint, value, max, placeholder, disabled, onChange }: { label: string; hint?: string; value: { en: string; fil: string }; max: number; placeholder?: string; disabled: boolean; onChange: (next: { en: string; fil: string }) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className="min-w-0 space-y-2" disabled={disabled}>
      <legend className="label">{label}</legend>
      {(["en", "fil"] as const).map((lang) => (
        <div key={lang} className="flex items-center gap-2">
          <span className="w-16 shrink-0 text-xs font-semibold text-fg-muted">{t(`${K}.content.${lang}`)}</span>
          <Input
            value={value[lang]}
            maxLength={max}
            placeholder={lang === "en" ? placeholder : undefined}
            aria-label={`${label} (${t(`${K}.content.${lang}`)})`}
            onChange={(event) => {
              onChange({ ...value, [lang]: event.target.value });
            }}
          />
        </div>
      ))}
      {hint ? <p className="hint">{hint}</p> : null}
    </fieldset>
  );
}

/** A picture: preview, upload (PNG/JPEG/WebP up to 2 MB, same path as Widget Studio) and remove. */
function ImageField({ label, hint, url, agentId, disabled, onChange, round }: { label: string; hint?: string; url: string | null | undefined; agentId: string; disabled: boolean; onChange: (url: string | null) => void; round?: boolean }) {
  const { t } = useTranslation();
  const message = useUploadErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const upload = useBrandImageUpload(agentId);
  const pick = (file: File | undefined) => {
    if (!file) return;
    if (imageProblem(file) !== null) {
      toast.error(t(`${K}.branding.invalid`));
      return;
    }
    upload.mutate(file, {
      onSuccess: ({ url: uploaded }) => {
        onChange(uploaded);
        toast.success(t(`${K}.branding.uploaded`, { name: file.name }));
      },
      onError: (error) => toast.error(message(error, t(`${K}.branding.failed`))),
    });
  };
  return (
    <div>
      <span className="label">{label}</span>
      <div className="flex flex-wrap items-center gap-3">
        {url ? (
          <img src={url} alt={label} className={cn("size-10 border border-line bg-surface object-cover", round ? "rounded-pill" : "rounded-md")} />
        ) : (
          <span aria-hidden="true" className={cn("inline-flex size-10 items-center justify-center border border-dashed border-line-strong text-fg-subtle", round ? "rounded-pill" : "rounded-md")}>
            <Icon name="image" className="size-4" />
          </span>
        )}
        <input ref={input} type="file" data-upload={label} accept={AVATAR_MIME_TYPES.join(",")} className="sr-only" tabIndex={-1} aria-hidden="true" onChange={(event) => { pick(event.target.files?.[0]); event.target.value = ""; }} />
        <Button variant="secondary" size="sm" disabled={disabled} loading={upload.isPending} onClick={() => input.current?.click()} aria-label={`${url ? t(`${K}.branding.replace`) : t(`${K}.branding.upload`)} ${label}`}>
          <Icon name="upload" />
          {upload.isPending ? t(`${K}.branding.uploading`) : url ? t(`${K}.branding.replace`) : t(`${K}.branding.upload`)}
        </Button>
        {url ? (
          <Button variant="ghost" size="sm" disabled={disabled} onClick={() => { onChange(null); }} aria-label={`${t(`${K}.branding.remove`)} ${label}`}>
            {t(`${K}.branding.remove`)}
          </Button>
        ) : null}
      </div>
      {hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

function IconChoice({ label, value, options, disabled, onChange }: { label: string; value: string; options: readonly { value: string; label: string; icon: IconName | null }[]; disabled: boolean; onChange: (next: string) => void }) {
  return (
    <div>
      <span className="label" id={`${label}-label`}>{label}</span>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              title={option.label}
              disabled={disabled}
              onClick={() => { onChange(option.value); }}
              className={cn("inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60", selected ? "border-primary bg-primary-soft text-primary-ink" : "border-line-strong bg-surface hover:bg-sunken")}
            >
              {option.icon ? <Icon name={option.icon} className="size-4" /> : null}
              {option.icon ? null : option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const iconOption = (t: (key: string) => string, name: IconName) => ({ value: name, label: t(`${K}.icons.icons.${name}`), icon: name });

export function BrandingPanel({ settings, onChange, design, disabled, isDefault, agentId }: PanelProps) {
  const { t } = useTranslation();
  const b = settings.branding;
  const set = (patch: Partial<typeof b>) => { onChange({ ...settings, branding: { ...b, ...patch } }); };
  const custom = b.accent !== null && b.accent !== undefined;
  return (
    <div className="space-y-5">
      <Row label={t(`${K}.branding.appName`)} hint={t(`${K}.branding.appNameHint`)} htmlFor="webapp-app-name">
        <Input id="webapp-app-name" value={b.app_name} maxLength={WEBAPP_APP_NAME_MAX_CHARS} placeholder={t(`${K}.branding.appNamePlaceholder`)} onChange={(event) => { set({ app_name: event.target.value }); }} />
      </Row>
      {isDefault ? <p className="hint">{t(`${K}.branding.noPictures`)}</p> : (
        <>
          <ImageField label={t(`${K}.branding.logo`)} hint={t(`${K}.branding.logoHint`)} url={b.logo_url ?? null} agentId={agentId} disabled={disabled} onChange={(url) => { set({ logo_url: url }); }} />
          <div>
            <span className="label">{t(`${K}.branding.avatar`)}</span>
            <Segmented
              aria-label={t(`${K}.branding.avatar`)}
              value={b.avatar_source}
              onValueChange={(next) => { set({ avatar_source: next }); }}
              options={[
                { value: "widget", label: t(`${K}.branding.avatarWidget`) },
                { value: "custom", label: t(`${K}.branding.avatarCustom`) },
                { value: "initials", label: t(`${K}.branding.avatarInitials`) },
              ]}
            />
            <p className="hint">{t(`${K}.branding.avatarHint`)}</p>
            {b.avatar_source === "custom" ? (
              <div className="mt-3">
                <ImageField label={t(`${K}.branding.avatar`)} url={b.avatar_url ?? null} agentId={agentId} disabled={disabled} round onChange={(url) => { set({ avatar_url: url }); }} />
              </div>
            ) : null}
          </div>
          <ImageField label={t(`${K}.branding.favicon`)} hint={t(`${K}.branding.faviconHint`)} url={b.favicon_url ?? null} agentId={agentId} disabled={disabled} onChange={(url) => { set({ favicon_url: url }); }} />
        </>
      )}
      <div>
        <span className="label">{t(`${K}.branding.accent`)}</span>
        <Segmented
          aria-label={t(`${K}.branding.accent`)}
          value={custom ? "custom" : "widget"}
          onValueChange={(next) => { set({ accent: next === "custom" ? design.widget.primary_color : null }); }}
          options={[
            { value: "widget", label: t(`${K}.branding.accentSame`) },
            { value: "custom", label: t(`${K}.branding.accentCustom`) },
          ]}
        />
        {custom ? (
          <div className="mt-3">
            <BrandColorPicker value={b.accent ?? design.widget.primary_color} onChange={(hex) => { set({ accent: hex }); }} serverWarning={design.contrast.warning} disabled={disabled} />
          </div>
        ) : (
          <p className="hint">{t(`${K}.branding.accentHint`)}</p>
        )}
      </div>
      <div>
        <span className="label">{t(`${K}.branding.theme`)}</span>
        <Segmented
          aria-label={t(`${K}.branding.theme`)}
          value={b.theme}
          onValueChange={(next) => { set({ theme: next }); }}
          options={[
            { value: "system", label: t(`${K}.branding.themeSystem`) },
            { value: "light", label: t(`${K}.branding.themeLight`) },
            { value: "dark", label: t(`${K}.branding.themeDark`) },
          ]}
        />
        <p className="hint">{t(`${K}.branding.themeHint`)}</p>
      </div>
      <div>
        <span className="label">{t(`${K}.branding.density`)}</span>
        <Segmented
          aria-label={t(`${K}.branding.density`)}
          value={b.density}
          onValueChange={(next) => { set({ density: next }); }}
          options={[
            { value: "comfortable", label: t(`${K}.branding.comfortable`) },
            { value: "compact", label: t(`${K}.branding.compact`) },
          ]}
        />
      </div>
      <div>
        <span className="label">{t(`${K}.branding.radius`)}</span>
        <Segmented
          aria-label={t(`${K}.branding.radius`)}
          value={b.radius_px === null || b.radius_px === undefined ? "widget" : "custom"}
          onValueChange={(next) => { set({ radius_px: next === "custom" ? design.widget.radius_px : null }); }}
          options={[
            { value: "widget", label: t(`${K}.branding.radiusWidget`) },
            { value: "custom", label: t(`${K}.branding.radiusCustom`) },
          ]}
        />
        {b.radius_px !== null && b.radius_px !== undefined ? (
          <div className="mt-3 flex items-center gap-3">
            <input type="range" min={0} max={28} value={b.radius_px} disabled={disabled} aria-label={t(`${K}.branding.radius`)} onChange={(event) => { set({ radius_px: Number(event.target.value) }); }} className="min-h-11 w-48 accent-[var(--wg-primary,currentColor)]" />
            <span className="text-sm font-semibold">{t(`${K}.branding.radiusValue`, { px: b.radius_px })}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function IconsPanel({ settings, onChange, disabled }: PanelProps) {
  const { t } = useTranslation();
  const tr = (key: string) => t(key);
  const icons = settings.icons;
  const set = (patch: Partial<typeof icons>) => { onChange({ ...settings, icons: { ...icons, ...patch } }); };
  const names = WEBAPP_ICON_VALUES.map((name) => iconOption(tr, name));
  return (
    <div className="space-y-5">
      <p className="hint">{t(`${K}.icons.hint`)}</p>
      <IconChoice label={t(`${K}.icons.assistantIcon`)} value={icons.assistant_icon} disabled={disabled} onChange={(next) => { set({ assistant_icon: next }); }} options={[{ value: WEBAPP_ASSISTANT_ICON_AVATAR, label: t(`${K}.icons.assistantAvatar`), icon: null }, ...names]} />
      <IconChoice label={t(`${K}.icons.cardIcon`)} value={icons.card_icon} disabled={disabled} onChange={(next) => { set({ card_icon: next }); }} options={[{ value: WEBAPP_CARD_ICON_AUTO, label: t(`${K}.icons.cardAuto`), icon: null }, { value: WEBAPP_CARD_ICON_NONE, label: t(`${K}.icons.cardNone`), icon: null }, ...names]} />
      <div>
        <span className="label">{t(`${K}.icons.sendStyle`)}</span>
        <Segmented
          aria-label={t(`${K}.icons.sendStyle`)}
          value={icons.send_style}
          onValueChange={(next) => { set({ send_style: next }); }}
          options={[
            { value: "icon", label: t(`${K}.icons.sendIcon`) },
            { value: "label", label: t(`${K}.icons.sendLabel`) },
          ]}
        />
      </div>
    </div>
  );
}

export function ContentPanel({ settings, onChange, design, disabled }: PanelProps) {
  const { t } = useTranslation();
  const content = settings.content;
  const set = (patch: Partial<typeof content>) => { onChange({ ...settings, content: { ...content, ...patch } }); };
  const local = (key: LocalKey) => (next: { en: string; fil: string }) => { set({ [key]: next }); };
  const starters = content.starters ?? null;
  const items = starters ?? [];
  return (
    <div className="space-y-5">
      <p className="hint">{t(`${K}.content.hint`)}</p>
      <LocalFields label={t(`${K}.content.title`)} value={content.title} max={WEBAPP_TITLE_MAX_CHARS} placeholder={t(`${K}.content.titlePlaceholder`)} disabled={disabled} onChange={local("title")} />
      <LocalFields label={t(`${K}.content.subtitle`)} value={content.subtitle} max={WEBAPP_LINE_MAX_CHARS} disabled={disabled} onChange={local("subtitle")} />
      <LocalFields label={t(`${K}.content.placeholder`)} value={content.placeholder} max={WEBAPP_PLACEHOLDER_MAX_CHARS} disabled={disabled} onChange={local("placeholder")} />
      <LocalFields label={t(`${K}.content.disclaimer`)} value={content.disclaimer} max={WEBAPP_NOTE_MAX_CHARS} disabled={disabled} onChange={local("disclaimer")} />
      <div>
        <span className="label">{t(`${K}.content.starters`)}</span>
        <Segmented
          aria-label={t(`${K}.content.starters`)}
          value={starters === null ? "widget" : "custom"}
          onValueChange={(next) => { set({ starters: next === "custom" ? [...design.widget.starter_questions] : null }); }}
          options={[
            { value: "widget", label: t(`${K}.content.startersWidget`) },
            { value: "custom", label: t(`${K}.content.startersCustom`) },
          ]}
        />
        {starters === null ? (
          design.widget.starter_questions.length === 0 ? <p className="hint">{t(`${K}.content.startersEmpty`)}</p> : (
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-fg-muted">
              {design.widget.starter_questions.map((question) => <li key={question}>{question}</li>)}
            </ol>
          )
        ) : (
          <div className="mt-3 space-y-2">
            {items.map((question, index) => (
              <div key={index} className="flex items-center gap-1.5">
                <Input value={question} maxLength={WEBAPP_STARTER_MAX_CHARS} disabled={disabled} aria-label={t(`${K}.content.starterLabel`, { n: index + 1 })} onChange={(event) => { set({ starters: items.map((item, i) => (i === index ? event.target.value : item)) }); }} />
                <Button variant="ghost" size="sm" iconOnly disabled={disabled || index === 0} aria-label={t(`${K}.content.moveUp`, { n: index + 1 })} onClick={() => { set({ starters: moveItem(items, index, index - 1) }); }}><Icon name="chevron-up" /></Button>
                <Button variant="ghost" size="sm" iconOnly disabled={disabled || index === items.length - 1} aria-label={t(`${K}.content.moveDown`, { n: index + 1 })} onClick={() => { set({ starters: moveItem(items, index, index + 1) }); }}><Icon name="chevron-down" /></Button>
                <Button variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${K}.content.removeStarter`, { n: index + 1 })} onClick={() => { set({ starters: items.filter((_, i) => i !== index) }); }}><Icon name="trash" /></Button>
              </div>
            ))}
            <Button variant="secondary" size="sm" disabled={disabled || items.length >= WEBAPP_MAX_STARTERS} onClick={() => { set({ starters: [...items, ""] }); }}>
              <Icon name="plus" />
              {t(`${K}.content.addStarter`)}
            </Button>
            <p className="hint">{t(`${K}.content.startersMax`, { max: WEBAPP_MAX_STARTERS })}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function LayoutPanel({ settings, onChange, design, disabled }: PanelProps) {
  const { t } = useTranslation();
  const layout = settings.layout;
  const set = (patch: Partial<typeof layout>) => { onChange({ ...settings, layout: { ...layout, ...patch } }); };
  const locked = !design.can_remove_branding;
  return (
    <div className="space-y-5">
      <SettingSwitch title={t(`${K}.layout.showHistory`)} hint={t(`${K}.layout.showHistoryHint`)} checked={layout.show_history} disabled={disabled} onCheckedChange={(checked) => { set({ show_history: checked }); }} />
      <LocalFields label={t(`${K}.layout.guestNotice`)} hint={t(`${K}.layout.guestNoticeHint`)} value={layout.guest_notice} max={WEBAPP_NOTE_MAX_CHARS} disabled={disabled} onChange={(next) => { set({ guest_notice: next }); }} />
      <div>
        <span className="label">{t(`${K}.layout.poweredBy`)}</span>
        <Segmented
          aria-label={t(`${K}.layout.poweredBy`)}
          value={layout.powered_by}
          onValueChange={(next) => {
            if (next !== "hide" || !locked) set({ powered_by: next });
          }}
          options={[
            { value: "widget", label: t(`${K}.layout.poweredByWidget`) },
            { value: "show", label: t(`${K}.layout.poweredByShow`) },
            { value: "hide", label: t(`${K}.layout.poweredByHide`) },
          ]}
        />
        <p className="hint">{locked ? t(`${K}.layout.poweredByLocked`) : t(`${K}.layout.poweredByHint`)}</p>
      </div>
    </div>
  );
}

export function SharePanel({ settings, onChange, design, disabled }: PanelProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <SettingSwitch title={t(`${K}.share.enabled`)} hint={t(`${K}.share.enabledHint`)} checked={settings.share.enabled} disabled={disabled} onCheckedChange={(checked) => { onChange({ ...settings, share: { enabled: checked } }); }} />
      <Row label={t(`${K}.share.link`)} hint={t(`${K}.share.linkHint`)} htmlFor="webapp-share-link">
        <Input id="webapp-share-link" readOnly value={design.share_url} className="text-xs" />
      </Row>
    </div>
  );
}
